import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import {
  isGlobalAudienceEnabled,
  isMultilangPocEnabled
} from '../../../constants/featureFlags'
import { resolveTranslationsSurveyId } from '../../../containers/Page/MultiMarket/TranslationsPage/translationsScope'
import {
  AudienceData,
  GET_AUDIENCES
} from '../../../data/gql/questionnaire/queries/audiences'
import { useMarketsSpecification } from '../../../hooks/audience/useMarketsSpecification'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { getLanguageLabel } from '../../../utils/HelperFunctions'
import { visibleInAnyMarket } from '../../Questionnaire/MarketVisibility/marketVisibility'
import { computeCompletion } from '../lib/completion'
import { extractCustomAudienceMembers } from '../lib/extractAudienceFields'
import { getTranslatableSurveyEntries } from '../lib/extractTranslatableFields'

// English is the master/source language the survey is authored in; it can
// never be "untranslated", so it's excluded from every market's check (see
// useTranslationsCompletenessValidation).
const MASTER_LANGUAGE = 'en'

export interface MarketTranslationWarning {
  surveyId: string
  /** ISO country code of the market, e.g. "AR". */
  country: string
  languageCode: string
  /** Display name of the language, e.g. "Spanish". */
  language: string
  /** Number of fields still untranslated for this market + language. */
  untranslated: number
}

export interface MarketsTranslationWarningsResult {
  warnings: ReadonlyArray<MarketTranslationWarning>
  /** Survey ids of markets that have at least one untranslated language. */
  incompleteSurveyIds: ReadonlySet<string>
}

const EMPTY: MarketsTranslationWarningsResult = {
  warnings: [],
  incompleteSurveyIds: new Set()
}

interface Market {
  surveyId: string
}

/**
 * Per-market translation warnings for the Confirm page: every allocated
 * non-English language below 100% produces one, e.g. "AR/Spanish: 1
 * untranslated". Reads the same data the Translations editor does — entries
 * from QuestionnaireContext, global screeners from the isBase market — so the
 * two surfaces always agree. `languagesBySurveyId` / `countryBySurveyId` come
 * from `useMarketsCompletes` to avoid refetching fieldwork. Behind
 * isMultilangPocEnabled().
 */
export const useMarketsTranslationWarnings = (
  projectId: string,
  masterSurveyId: string | undefined,
  markets: ReadonlyArray<Market>,
  languagesBySurveyId: ReadonlyMap<string, string[]>,
  countryBySurveyId: ReadonlyMap<string, string>
): MarketsTranslationWarningsResult => {
  const waveId = useOptionalWaveId()
  const globalAudienceEnabled = isGlobalAudienceEnabled()
  // Audience translations are per market, so read them from the survey the
  // editor writes to — the isBase market, which is not the master (the oldest
  // survey): isBase moves to whichever market last globalised a question.
  const { baseSurveyId, loading: baseMarketLoading } = useMarketsSpecification(
    globalAudienceEnabled ? masterSurveyId : undefined
  )
  const audienceSurveyId = resolveTranslationsSurveyId({
    globalAudienceEnabled,
    masterSurveyId,
    baseSurveyId,
    baseMarketLoading
  })

  // Undefined while the base resolves, so no warning renders off a stale scope.
  const skip = !isMultilangPocEnabled() || !audienceSurveyId

  // Read the questionnaire from the same source the Translations editor uses
  // (QuestionnaireContext, scoped to the master via the Confirm route's
  // surveyId) so completeness here matches the editor exactly, rather than
  // issuing a parallel query that can resolve to different data.
  const { draftQuestionnaire } = useGetDraftQuestionnaire()

  const { data: audienceData } = useQuery<AudienceData>(GET_AUDIENCES, {
    context: { clientName: 'questionnaire' },
    variables: { projectId, surveyId: audienceSurveyId, waveId },
    skip
  })

  const entries = draftQuestionnaire?.entries
  const members = audienceData?.draftAudience.members

  return useMemo<MarketsTranslationWarningsResult>(() => {
    if (skip) return EMPTY

    // Only global screeners are shared; local ones are authored per market.
    const audienceGroups = extractCustomAudienceMembers(members ?? [], {
      globalOnly: globalAudienceEnabled
    })

    const warnings: MarketTranslationWarning[] = []
    const incompleteSurveyIds = new Set<string>()

    for (const { surveyId } of markets) {
      const languages = languagesBySurveyId.get(surveyId) ?? []
      const country = countryBySurveyId.get(surveyId) ?? ''
      // Scoped to this market's respondents: a UK-only question needs no
      // German translation just because Germany is also fielded.
      const isVisible = visibleInAnyMarket([surveyId])
      const translatableEntries = getTranslatableSurveyEntries(
        entries ?? [],
        isVisible
      )
      for (const languageCode of languages) {
        if (languageCode === MASTER_LANGUAGE) continue
        // Compare raw counts, not the rounded percentage, so a single missing
        // field still surfaces (computeCompletion rounds pct for display).
        const stats = computeCompletion(
          translatableEntries,
          languageCode,
          audienceGroups,
          isVisible
        )
        if (stats.translated < stats.total) {
          warnings.push({
            surveyId,
            country,
            languageCode,
            language: getLanguageLabel(languageCode),
            untranslated: stats.total - stats.translated
          })
          incompleteSurveyIds.add(surveyId)
        }
      }
    }

    return { warnings, incompleteSurveyIds }
  }, [
    skip,
    entries,
    members,
    markets,
    languagesBySurveyId,
    countryBySurveyId,
    globalAudienceEnabled
  ])
}

export default useMarketsTranslationWarnings
